// Central language + derivation layer for the Data Seamless redesign.
//
// Spec §3 ("lenguaje transversal") and §8 ("colores y estados") demand two things this
// module is the single source of:
//   1. State (dato) is translated to a human label, kept SEPARATE from methodological
//      impact ("Falta" is the state; "Bloquea LCA · EcoScore" is the impact).
//   2. Colour communicates SEVERITY / attention, never provenance. "Asumido" is not red.
//
// Everything here derives from data the existing endpoints already return; no new business
// logic. It builds on methodologyUtils (criticalFor, methodologyStats, METH_ORDER) so the
// criticality rules live in one place only.

import { criticalFor, methodologyStats, METH_ORDER } from "./methodologyUtils";

// ─────────────────────────────────────────────────────────────────────────────
// 1. Gap status → user-facing label (spec §3 table, verbatim)
// ─────────────────────────────────────────────────────────────────────────────
export const STATE_LABEL = {
  MISSING: "Falta",
  BLOCKING: "Falta (crítico)",
  ASSUMPTION_PENDING: "Puedes asumir",
  UNRESOLVED_MAPPING: "Necesita revisión",
  REQUESTED: "Marcado como pedido",
  RESOLVED_PROVIDED: "Aportado",
  RESOLVED_ASSUMPTION: "Asumido",
  ASSUMPTION_REJECTED: "Rechazado — pendiente de dato real",
  INVALID_VALUE: "Dato inválido",
  EXPIRED: "Caducado",
};

export const stateLabel = (status) => STATE_LABEL[status] || status || "—";

// ─────────────────────────────────────────────────────────────────────────────
// 2. Severity model (spec §8) — the ONLY thing that drives colour
// ─────────────────────────────────────────────────────────────────────────────
// rojo → bloqueo/error crítico · ámbar → requiere intervención · verde → completo/ready
// gris/neutro → información, no aplicable, procedencia.
export const SEVERITY = {
  CRITICAL: "critical",
  WARN: "warn",
  OK: "ok",
  NEUTRAL: "neutral",
};

export const SEVERITY_COLOR = {
  critical: "#dc2626", // rojo
  warn: "#d97706", // ámbar
  ok: "#059669", // verde
  neutral: "#64748b", // gris/neutro
};

// State → severity. Note the deliberate choices per §8:
//  - RESOLVED_ASSUMPTION ("Asumido") is OK/verde, never red: provenance ≠ severity.
//  - INVALID_VALUE and UNRESOLVED_MAPPING both need intervention → ámbar (they carry
//    distinct labels — "Dato inválido" / "Necesita revisión" — so text disambiguates).
//  - Only BLOCKING is red at the *state* level; whether a MISSING datum blocks a
//    methodology is a separate concept surfaced by impactLabel(), not by colour.
const STATE_SEVERITY = {
  MISSING: SEVERITY.WARN,
  BLOCKING: SEVERITY.CRITICAL,
  ASSUMPTION_PENDING: SEVERITY.WARN,
  UNRESOLVED_MAPPING: SEVERITY.WARN,
  INVALID_VALUE: SEVERITY.WARN,
  ASSUMPTION_REJECTED: SEVERITY.WARN,
  REQUESTED: SEVERITY.NEUTRAL,
  EXPIRED: SEVERITY.NEUTRAL,
  RESOLVED_PROVIDED: SEVERITY.OK,
  RESOLVED_ASSUMPTION: SEVERITY.OK,
};

export const severityOf = (status) => STATE_SEVERITY[status] || SEVERITY.NEUTRAL;
export const stateColor = (status) => SEVERITY_COLOR[severityOf(status)];

// ─────────────────────────────────────────────────────────────────────────────
// 3. Project-level state (spec §4.2) — Ready / Con assumptions / Incompleto / Bloqueado
// ─────────────────────────────────────────────────────────────────────────────
// Primary state = worst readiness across the given (contracted) methodologies.
// Reuses methodologyStats so the per-methodology rollup logic is not duplicated.
export const PROJECT_STATE = {
  READY: { key: "READY", label: "Ready", severity: SEVERITY.OK },
  WITH_ASSUMPTIONS: { key: "WITH_ASSUMPTIONS", label: "Con assumptions", severity: SEVERITY.OK },
  INCOMPLETE: { key: "INCOMPLETE", label: "Incompleto", severity: SEVERITY.WARN },
  BLOCKED: { key: "BLOCKED", label: "Bloqueado", severity: SEVERITY.CRITICAL },
};

// articles: the /completeness rows (each with methodologies[]). methodologies: display
// names to consider (contracted ∩ present). Returns the descriptor + colour, or null when
// there is nothing to assess (no data yet).
export const projectStateOf = (articles, methodologies) => {
  const meths = (methodologies && methodologies.length ? methodologies : METH_ORDER) || [];
  let anyCounted = false;
  let blocked = 0;
  let incomplete = 0;
  let withAssum = 0;
  meths.forEach((m) => {
    const s = methodologyStats(articles, m);
    if (!s.count) return;
    anyCounted = true;
    blocked += s.blocked;
    incomplete += s.incomplete;
    withAssum += s.withAssum;
  });
  if (!anyCounted) return null;
  const state =
    blocked > 0
      ? PROJECT_STATE.BLOCKED
      : incomplete > 0
      ? PROJECT_STATE.INCOMPLETE
      : withAssum > 0
      ? PROJECT_STATE.WITH_ASSUMPTIONS
      : PROJECT_STATE.READY;
  return { ...state, color: SEVERITY_COLOR[state.severity], blocked, incomplete, withAssum };
};

// Short sentence under the project state (spec §4.2 subtitle).
export const projectStateHint = (state) => {
  if (!state) return "";
  switch (state.key) {
    case "BLOCKED":
      return "Faltan datos críticos que impiden completar algunas metodologías.";
    case "INCOMPLETE":
      return "Hay información pendiente antes de completar algunas metodologías.";
    case "WITH_ASSUMPTIONS":
      return "El proyecto puede calcularse apoyándose en valores asumidos.";
    default:
      return "Toda la información necesaria está disponible.";
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. Methodological impact (spec §3) — kept separate from state
// ─────────────────────────────────────────────────────────────────────────────
// The methodologies a field is required for (M / M-A) come from criticalFor(). When `allowed`
// (the contracted methodologies) is given, the list is narrowed to those — we never surface an
// impact on a methodology the account has not contracted.
export const impactMethodologies = (field, allowed) => {
  const m = criticalFor(field);
  return allowed && allowed.length ? m.filter((x) => allowed.includes(x)) : m;
};

// verb: "Bloquea" (default, when the datum is absent/invalid) or "Afecta a" (softer, e.g.
// translations under review). Returns "" when the field is not required for any methodology.
export const impactLabel = (methodologies, { verb = "Bloquea" } = {}) => {
  const list = methodologies || [];
  if (!list.length) return "";
  return `${verb} ${list.join(" · ")}`;
};

// Convenience: impact string straight from a groupBy=field row. opts: { allowed, verb }.
export const fieldImpactLabel = (field, opts = {}) =>
  impactLabel(impactMethodologies(field, opts.allowed), opts);

// Is this field REQUIRED (M / M-A) for at least one of the contracted methodologies?
// This is the "necesita atención" gate: a field only Recommended (R) for everything the
// account contracts is optional — surfacing it as pending work is what overwhelms the user
// (spec §3: estado ≠ impacto; readiness only counts M/M-A). Fields with no requirement data
// are treated as NOT required (optional) so they never inflate the pending count.
export const isFieldRequired = (field, allowed) =>
  impactMethodologies(field, allowed).length > 0;
